import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Brain, Upload, Loader2, Cpu, FileText, X, ArrowLeft, Sparkles, RefreshCw,
  ClipboardList, ShieldCheck, CalendarClock, Settings2, BadgeCheck, CheckCircle2,
  AlertCircle, HelpCircle, Quote, ExternalLink, Download,
} from "lucide-react";

const AI_MODELS = [
  { value: "gpt-4o", label: "GPT-4o — highest accuracy" },
  { value: "gpt-4o-mini", label: "GPT-4o mini — faster & cheaper" },
];

const SECTION_META = {
  documents_required: { icon: ClipboardList, color: "text-indigo-600", bg: "bg-indigo-50", ring: "ring-indigo-100" },
  eligibility: { icon: ShieldCheck, color: "text-emerald-600", bg: "bg-emerald-50", ring: "ring-emerald-100" },
  dates: { icon: CalendarClock, color: "text-rose-600", bg: "bg-rose-50", ring: "ring-rose-100" },
  technical: { icon: Settings2, color: "text-sky-600", bg: "bg-sky-50", ring: "ring-sky-100" },
  compliance: { icon: BadgeCheck, color: "text-amber-600", bg: "bg-amber-50", ring: "ring-amber-100" },
};

const VERDICT_STYLE = {
  "Likely Eligible": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Partially Eligible": "bg-amber-100 text-amber-700 border-amber-200",
  "Not Eligible": "bg-red-100 text-red-700 border-red-200",
  "Needs Review": "bg-slate-100 text-slate-700 border-slate-200",
};

const STATUS_META = {
  meets: { icon: CheckCircle2, cls: "text-emerald-600", label: "Meets" },
  gap: { icon: AlertCircle, cls: "text-red-600", label: "Gap" },
  unclear: { icon: HelpCircle, cls: "text-slate-400", label: "Unclear" },
};

function Citation({ source, onOpen }) {
  if (!source || (!source.document && !source.section)) return null;
  const page = source.page !== null && source.page !== undefined ? ` · p.${source.page}` : "";
  const label = source.document ? `${source.document}${page}` : source.section;
  const clickable = !!(onOpen && source.document);
  return (
    <button
      type="button"
      onClick={clickable ? onOpen : undefined}
      disabled={!clickable}
      className={`group inline-flex items-center gap-1 mt-1.5 text-[11px] rounded-md px-1.5 py-0.5 max-w-full transition-colors ${
        clickable
          ? "text-emerald-700 bg-emerald-50 hover:bg-emerald-100 cursor-pointer"
          : "text-slate-500 bg-slate-100 cursor-default"
      }`}
      title={clickable ? `Open ${source.document}${page} — ${source.section || "source"}` : (source.section || source.document)}
      data-testid="intel-citation"
    >
      <Quote className="h-3 w-3 shrink-0" />
      <span className="truncate">{label}</span>
      {source.section ? <span className="opacity-70 truncate hidden sm:inline">· {source.section}</span> : null}
      {clickable ? <ExternalLink className="h-2.5 w-2.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" /> : null}
    </button>
  );
}

export default function BidIntel() {
  const { bidId } = useParams();
  const navigate = useNavigate();
  const [bid, setBid] = useState(null);
  const [intel, setIntel] = useState({ documents: [], analysis: null, ai_usage: null, analyzed_at: null });
  const [model, setModel] = useState("gpt-4o");
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const fileRef = useRef();

  const loadIntel = () => api.get(`/tracker/bids/${bidId}/intel`).then((r) => setIntel(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/tracker/bids").then((r) => setBid((r.data || []).find((b) => b.id === bidId) || null)).catch(() => {});
    loadIntel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidId]);

  // poll while any document is still being read in the background
  useEffect(() => {
    const anyProcessing = (intel.documents || []).some((d) => d.status === "processing");
    if (!anyProcessing) return;
    const t = setInterval(loadIntel, 2500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intel.documents]);

  // refresh when the tab regains focus (picks up docs that finished while away)
  useEffect(() => {
    const onFocus = () => loadIntel();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidId]);

  const docs = intel.documents || [];
  const readyCount = docs.filter((d) => d.status === "ready").length;
  const anyProcessing = docs.some((d) => d.status === "processing");

  const [viewer, setViewer] = useState(null); // { docId, filename, page, section }
  const docByName = useMemo(() => {
    const m = {};
    (intel.documents || []).forEach((d) => { if (!(d.filename in m)) m[d.filename] = d; });
    return m;
  }, [intel.documents]);
  const openSource = (source) => {
    const d = source?.document ? docByName[source.document] : null;
    if (!d) { toast.info("The original file is no longer attached to this analysis"); return; }
    setViewer({ docId: d.id, filename: d.filename, page: source.page, section: source.section });
  };
  const fileUrl = (docId, page) => {
    const base = `${API}/tracker/bids/${bidId}/intel/documents/${docId}/file`;
    return page ? `${base}#page=${page}&zoom=page-width` : base;
  };

  const onFiles = async (e) => {
    const chosen = Array.from(e.target.files || []);
    e.target.value = "";
    if (chosen.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      chosen.forEach((f) => fd.append("files", f));
      fd.append("model", model);
      const r = await api.post(`/tracker/bids/${bidId}/intel/documents`, fd);
      setIntel(r.data);
      if (r.data.skipped?.length) toast.warning(`Skipped: ${r.data.skipped.join("; ")}`);
      toast.success(`${chosen.length} document(s) added — reading them now…`);
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Could not upload documents");
    } finally {
      setUploading(false);
    }
  };

  const removeDoc = async (docId) => {
    try {
      const r = await api.delete(`/tracker/bids/${bidId}/intel/documents/${docId}`);
      setIntel(r.data);
      toast.success("Document removed");
    } catch { toast.error("Could not remove document"); }
  };

  const analyze = async () => {
    if (readyCount === 0) return toast.error("Add at least one readable document first");
    setAnalyzing(true);
    try {
      const r = await api.post(`/tracker/bids/${bidId}/intel/analyze`, { model });
      setIntel(r.data);
      toast.success("Analysis ready");
    } catch (e) {
      if (e.response?.status === 409) {
        toast.info("Documents are still being read — please wait a moment and try again");
        loadIntel();
      } else {
        toast.error(e.response?.data?.detail || "AI analysis failed");
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const analysis = intel.analysis;
  const usage = intel.ai_usage;

  return (
    <div className="px-6 lg:px-12 py-8 lg:py-12 max-w-6xl">
      <button onClick={() => navigate("/tracker")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="intel-back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to Bid Tracker
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-700">Bid Intelligence</div>
          <h1 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight mt-2 flex items-center gap-2">
            <Brain className="h-6 w-6 text-slate-900" /> {bid?.bid_number || "Bid Analysis"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Upload the bid / tender documents and let AI extract the requirements, eligibility and deadlines — each point cited to its source.
          </p>
        </div>
        {analysis && (
          <span className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${VERDICT_STYLE[analysis.eligibility_verdict] || VERDICT_STYLE["Needs Review"]}`} data-testid="intel-verdict">
            {analysis.eligibility_verdict}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 mt-6">
        {/* LEFT: documents + controls */}
        <div className="xl:col-span-4">
          <Card className="p-5 rounded-xl border-border bg-slate-900 text-white sticky top-6">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-amber-400" />
              <h3 className="font-head font-bold">Bid Documents</h3>
            </div>
            <p className="text-xs text-slate-300 mt-2 leading-relaxed">
              Add the ATC, tender, eligibility sheets or spec PDFs. Word, images and scanned pages are read too.
            </p>

            <div className="mt-4">
              <label className="flex items-center gap-1.5 text-[11px] text-slate-400 mb-1"><Cpu className="h-3.5 w-3.5" /> AI model</label>
              <select value={model} onChange={(e) => setModel(e.target.value)} className="w-full rounded-lg bg-white/5 border border-white/10 text-white text-xs px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber-400" data-testid="intel-model-select">
                {AI_MODELS.map((m) => <option key={m.value} value={m.value} className="text-slate-900">{m.label}</option>)}
              </select>
            </div>

            <input ref={fileRef} type="file" multiple accept=".pdf,.docx,.txt,.csv,.png,.jpg,.jpeg,.webp,.gif" hidden onChange={onFiles} data-testid="intel-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full mt-4 rounded-lg bg-white/10 hover:bg-white/20 border-0 text-white disabled:opacity-60" data-testid="intel-upload-btn">
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              {uploading ? "Uploading…" : "Choose documents"}
            </Button>
            <p className="mt-2 text-[11px] text-slate-400 leading-relaxed">
              Pick one or more files (hold Ctrl/⌘ to select several). They upload and start reading automatically.
            </p>

            {/* uploaded documents */}
            <div className="mt-4 border-t border-white/10 pt-4">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">In this analysis ({docs.length})</div>
              {docs.length === 0 ? (
                <div className="text-xs text-slate-500" data-testid="intel-no-docs">No documents added yet.</div>
              ) : (
                <div className="space-y-1.5" data-testid="intel-doc-list">
                  {docs.map((d) => (
                    <div key={d.id} className="flex items-center gap-2 text-xs bg-white/5 rounded-lg px-2.5 py-1.5" data-testid={`intel-doc-${d.id}`}>
                      {d.status === "processing" ? <Loader2 className="h-3.5 w-3.5 text-amber-300 shrink-0 animate-spin" />
                        : d.status === "error" ? <AlertCircle className="h-3.5 w-3.5 text-red-400 shrink-0" />
                          : <FileText className="h-3.5 w-3.5 text-emerald-300 shrink-0" />}
                      <span className="truncate flex-1">{d.filename}</span>
                      <span className="text-[10px] text-slate-400 shrink-0" title={d.error || ""} data-testid={`intel-doc-status-${d.id}`}>
                        {d.status === "processing" ? "Reading…" : d.status === "error" ? "Failed" : `${d.page_count}p`}
                      </span>
                      <button onClick={() => removeDoc(d.id)} className="text-slate-400 hover:text-red-400" data-testid={`intel-doc-remove-${d.id}`}><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button onClick={analyze} disabled={analyzing || readyCount === 0} className="w-full mt-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 border-0 disabled:opacity-50" data-testid="intel-analyze-btn">
              {analyzing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : analysis ? <RefreshCw className="h-4 w-4 mr-2" /> : <Brain className="h-4 w-4 mr-2" />}
              {analyzing ? "Analyzing…" : analysis ? "Regenerate Analysis" : "Analyze Documents"}
            </Button>
            {anyProcessing && (
              <p className="mt-2 text-[11px] text-amber-300/90 flex items-center gap-1.5" data-testid="intel-processing-note">
                <Loader2 className="h-3 w-3 animate-spin" /> Reading documents — you can analyze once they are ready.
              </p>
            )}
            {usage && (
              <div className="mt-3 text-[11px] text-amber-300 flex items-center gap-1.5" data-testid="intel-cost-meter">
                <Cpu className="h-3 w-3" /> {usage.model} · {usage.total_tokens.toLocaleString()} tok · ~${usage.cost_usd < 0.01 ? usage.cost_usd.toFixed(4) : usage.cost_usd.toFixed(3)}
              </div>
            )}
          </Card>
        </div>

        {/* RIGHT: analysis */}
        <div className="xl:col-span-8">
          {!analysis ? (
            <Card className="rounded-xl border-border p-10 text-center text-slate-400" data-testid="intel-empty">
              <Brain className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm max-w-sm mx-auto">
                Add your bid documents on the left, then click <strong>Analyze Documents</strong>. The AI reads everything together and produces a cited, section-by-section summary.
              </p>
            </Card>
          ) : (
            <div className="space-y-5" data-testid="intel-analysis">
              {analysis.truncated && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-800 flex items-start gap-2" data-testid="intel-truncated-note">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  These documents are large, so only the earlier pages were analyzed. For full coverage, split very long files or remove ones not needed for this bid.
                </div>
              )}
              {analysis.summary && (
                <Card className="rounded-xl border-border p-5 bg-slate-50" data-testid="intel-summary">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Overview</div>
                  <p className="text-sm text-slate-700 leading-relaxed">{analysis.summary}</p>
                </Card>
              )}

              {analysis.sections.map((sec) => {
                const meta = SECTION_META[sec.key] || SECTION_META.compliance;
                const Icon = meta.icon;
                return (
                  <Card key={sec.key} className="rounded-xl border-border overflow-hidden" data-testid={`intel-section-${sec.key}`}>
                    <div className={`flex items-center gap-2.5 px-5 py-3.5 border-b border-border ${meta.bg}`}>
                      <span className={`h-8 w-8 rounded-lg bg-white flex items-center justify-center ring-1 ${meta.ring}`}>
                        <Icon className={`h-4 w-4 ${meta.color}`} />
                      </span>
                      <h3 className="font-head font-bold text-sm text-slate-900">{sec.title}</h3>
                      <span className="ml-auto text-xs text-slate-400">{sec.items.length}</span>
                    </div>
                    {sec.items.length === 0 ? (
                      <div className="px-5 py-4 text-sm text-slate-400">Nothing found in the documents for this section.</div>
                    ) : (
                      <ul className="divide-y divide-slate-100">
                        {sec.items.map((it, i) => {
                          const st = it.status ? STATUS_META[it.status] : null;
                          const StIcon = st?.icon;
                          return (
                            <li key={i} className="px-5 py-3.5 flex gap-3" data-testid={`intel-item-${sec.key}-${i}`}>
                              {sec.key === "eligibility" && st ? (
                                <span className={`shrink-0 mt-0.5 ${st.cls}`} title={st.label}><StIcon className="h-4 w-4" /></span>
                              ) : (
                                <span className={`shrink-0 mt-1.5 h-1.5 w-1.5 rounded-full ${meta.color.replace("text-", "bg-")}`} />
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="text-sm text-slate-700 leading-relaxed">{it.text}</p>
                                <Citation source={it.source} onOpen={() => openSource(it.source)} />
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="max-w-5xl w-[94vw] h-[88vh] p-0 overflow-hidden flex flex-col" data-testid="intel-source-viewer">
          <DialogHeader className="px-5 py-3 border-b border-border shrink-0">
            <DialogTitle className="text-sm font-semibold flex items-center gap-2 pr-8">
              <FileText className="h-4 w-4 text-emerald-600 shrink-0" />
              <span className="truncate">{viewer?.filename}</span>
              {viewer?.page ? <span className="text-slate-400 font-normal shrink-0">· page {viewer.page}</span> : null}
              {viewer?.section ? <span className="text-slate-400 font-normal truncate hidden sm:inline">· {viewer.section}</span> : null}
            </DialogTitle>
          </DialogHeader>
          {viewer && (() => {
            const ext = (viewer.filename.split(".").pop() || "").toLowerCase();
            const url = fileUrl(viewer.docId, viewer.page);
            if (ext === "pdf") {
              return <iframe title="source" src={url} className="flex-1 w-full bg-slate-100" data-testid="intel-viewer-pdf" />;
            }
            if (["png", "jpg", "jpeg", "webp", "gif"].includes(ext)) {
              return (
                <div className="flex-1 overflow-auto bg-slate-100 flex items-start justify-center p-4">
                  <img src={fileUrl(viewer.docId)} alt={viewer.filename} className="max-w-full h-auto rounded shadow" data-testid="intel-viewer-image" />
                </div>
              );
            }
            return (
              <div className="flex-1 flex flex-col items-center justify-center gap-4 text-slate-500 p-8" data-testid="intel-viewer-fallback">
                <FileText className="h-10 w-10 opacity-40" />
                <p className="text-sm text-center max-w-sm">
                  This file type can't be previewed in-page{viewer.page ? ` (cited around page ${viewer.page})` : ""}. Open it to view the source.
                </p>
                <Button onClick={() => window.open(fileUrl(viewer.docId), "_blank")} className="rounded-lg bg-slate-900 hover:bg-slate-800" data-testid="intel-viewer-open">
                  <Download className="h-4 w-4 mr-2" /> Open {viewer.filename}
                </Button>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
